import { useTranslation } from "react-i18next";
import type { EditorialContent } from "@nh/shared";
import { ProductBlockHeading } from "./ProductBlockHeading";
import { ProductEditorialIcon } from "./ProductEditorialIcon";

export type ProductProcessSection = NonNullable<EditorialContent["process"]>;

export interface ProductProcessProps {
  section: ProductProcessSection;
  number?: string;
}

/**
 * El proceso: los pasos, en pila y numerados.
 *
 * ── El rótulo vuelve a ser "Mecanismo de acción" ─────────────────────
 * Antes decía "En detalle", y el argumento era bueno: el contenido REAL
 * que trae el catálogo de producción es una conclusión —"Respaldo
 * científico", "Alta pureza", "Sinergia única"—, no un mecanismo, así que
 * el rótulo contradecía al título que tiene justo debajo.
 *
 * Pero el original rotula "08 / Mecanismo de acción" sobre ese mismo
 * título, y eso es lo que se ve hoy en itsthebest1111.com. La ficha tiene
 * que leerse IGUAL, y una discrepancia de rótulo es de las que se notan
 * poniendo las dos pantallas al lado. El desajuste entre el rótulo y el
 * contenido es real y viene del catálogo: se arregla escribiendo un
 * `process.title` que sí describa un mecanismo, no renombrando el bloque
 * aquí para tapar el síntoma en los cuarenta y cinco productos.
 *
 * ── Apilado, no en rejilla ───────────────────────────────────────────
 * Un proceso tiene ORDEN, y una rejilla de tres columnas lo pierde: en
 * el teléfono se apila —01, 02, 03— y en escritorio se lee de izquierda a
 * derecha, así que la misma información se recorre de dos formas
 * distintas según la pantalla. En pila es siempre de arriba abajo.
 *
 * ── El número va en el círculo, no en el título ──────────────────────
 * "01" en monoespaciada con cero delante, sobre el violeta de marca. El
 * cero no es decorativo: alinea el 9 con el 10 sin que la columna baile.
 * Blanco sobre `ds-violet-pop` da 5.9:1.
 *
 * ── Y el icono se va al otro extremo, apagado ────────────────────────
 * El peso visual de la fila ya lo carga el número. Un segundo círculo
 * oscuro al lado del título competiría con él; el icono va suelto, en
 * `ds-ink-4` como en el original, como remate de la fila. Se esconde en
 * el teléfono: ahí la fila ya es estrecha y el icono le roba el ancho al
 * texto.
 */
export function ProductProcess({ section, number }: ProductProcessProps) {
  const { t } = useTranslation();

  return (
    <section className="grid gap-7">
      <ProductBlockHeading
        eyebrow={t("Mecanismo de acción")}
        number={number}
        title={section.title}
        intro={section.intro}
      />

      <ol className="relative grid gap-3 before:absolute before:bottom-6 before:left-[31px] before:top-6 before:w-px before:bg-ds-lavender-bg-2 sm:before:left-[35px]">
        {section.items.map((step, index) => (
          <li
            key={step.title}
            className="relative flex items-start gap-4 rounded-[24px] border border-ds-border bg-white px-4 py-[18px] shadow-ds-xs transition hover:shadow-ds-card sm:px-5"
          >
            <span
              aria-hidden="true"
              className="relative z-10 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-ds-violet-pop font-mono text-[11px] font-semibold text-white shadow-ds-cta"
            >
              {String(index + 1).padStart(2, "0")}
            </span>

            <div className="min-w-0 flex-1">
              <h3 className="text-[17px] font-semibold leading-[1.3] tracking-[-0.01em] text-ds-ink">
                {step.title}
              </h3>

              {step.description && (
                <p className="mt-1 text-[13px] font-light leading-[1.55] text-ds-ink-3">
                  {step.description}
                </p>
              )}
            </div>

            {/*
              El trazo va en `ink-4` como en el original: es un adorno, no
              un dato, y 3.48:1 cumple el 3:1 que se le pide a un gráfico.
            */}
            <span className="hidden self-center text-ds-ink-4 sm:block">
              <ProductEditorialIcon name={step.icon} variant="step" />
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}
